import '@/styles/globals.css';
import { createIntlClient } from '@/utils/intl';
import type { AppProps } from 'next/app';
import { Router } from 'next/router';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider, HydrationBoundary } from '@tanstack/react-query';
import { Suspense, useEffect, useRef, useState } from 'react';
import { HistoryProvider } from '@/history/historyProvider';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { useInitGrowthBook } from '@/hooks/useInitGrowthBook';
import { Poppins } from 'next/font/google';
import Analytics from '@/analytics/Analytics';
import NextNProgress from 'nextjs-progressbar';
import 'react-toastify/dist/ReactToastify.css';
import usePromoSource from '@/hooks/usePromoSource';
import { ExponeaScript, CookiesScript, JaraBotScript } from '@/externalScripts';
import { CareerConsolePromotion } from '@/sections/career/CareerConsolePromotion';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { logEvent } from '@/analytics/events';
import { useReportWebVitals } from 'next/web-vitals';
import { Language } from '@/constants/currencyConfig';
import { RootsContext, detectRoots } from 'next-roots/context';
import { useHreflangs } from '@/hooks/useHreflangs';
import schemaRoots from '../../roots.schema';
import { OrbisProvider } from '@mallpay/orbis';
import { banksList, defaultMessages } from '@/lang/defaultConstants';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { yieldToMain } from '@/utils/dom';
import { clarityEvaluateRoute, initClarity } from '@/utils/clarity';
import NextLink from 'next/link';
const poppins = Poppins({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin-ext'],
  display: 'swap',
  variable: '--font-poppins'
});
Router.events.on('routeChangeStart', () => {
  if (window.skippay) {
    window.skippay.pageLoaded = false;
  }
});
Router.events.on('routeChangeComplete', (url: string) => {
  if (window.skippay) {
    window.skippay.pageLoaded = true;
  }
  clarityEvaluateRoute(url);
});

// Create a client-side GrowthBook instance
const gb = new GrowthBook({
  apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
  clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
  enableDevMode: process.env.NEXT_PUBLIC_TARGET_ENV === 'accept' || process.env.NEXT_PUBLIC_TARGET_ENV === 'nightly',
  attributes: {
    environment: process.env.NODE_ENV === 'production' ? 'production' : 'accept'
  },
  trackingCallback: (experiment, result) => {
    yieldToMain(() => {
      window.dataLayer?.push({
        event: 'experiment_viewed',
        content_name: result.name,
        content_id: `${experiment.key}:${result.variationId}`
      });
    });
  }
});
const ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools').then(d => ({
  default: d.ReactQueryDevtools
})), {
  ssr: false
});
const RootsConsoleProduction = dynamic(() => import('next-roots/console').then(d => ({
  default: d.RootsConsole
})), {
  ssr: false
});
interface Window {
  toggleDevtools: () => void;
}
const ToastContainerLazy = dynamic(() => import('react-toastify').then(d => ({
  default: d.ToastContainer
})), {
  ssr: false
});
export default function App(appProps: AppProps) {
  useReportWebVitals(metric => {
    const sampleRate = 0.1;
    if (Math.random() < sampleRate) {
      logEvent('web_vitals', metric);
    }
  });
  const defaultLanguage: Language = process.env.NEXT_PUBLIC_LANGUAGE || 'cs';
  const roots = detectRoots(appProps, {
    defaultLocale: schemaRoots.defaultLocale,
    locales: schemaRoots.locales
  });
  const {
    Component,
    pageProps,
    router
  } = appProps;
  const i18nClient = createIntlClient({
    ...pageProps.i18n,
    lng: defaultLanguage
  });
  const queryClientRef = useRef<QueryClient>(new QueryClient());
  const [showDevtools, setShowDevtools] = useState<boolean>(process.env.NEXT_PUBLIC_TARGET_ENV !== 'prod');
  const hrefLangLinks = useHreflangs(roots, router);
  useInitGrowthBook(gb, router);
  usePromoSource();
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_TARGET_ENV !== 'prod') {
      // Expose React Query Devtools globally on dev site
      (window as unknown as Window).toggleDevtools = () => setShowDevtools(old => !old);
    }
    if (window.skippay) {
      window.skippay.pageLoaded = true;
    } else {
      window.skippay = {
        pageLoaded: true
      };
    }
    initClarity();
  }, []);
  return <RootsContext.Provider value={roots}>
			<Head>
				<meta name="viewport" content="width=device-width, initial-scale=1" />

				<style>
					{`
						:root {
							--font-poppins: ${poppins.style.fontFamily}
						}
					`}
				</style>

				{hrefLangLinks.map(link => <link key={link.href} rel="alternate" hrefLang={link.hrefLang} href={link.href} />)}
			</Head>

			<ExponeaScript />
			<CookiesScript />
			<JaraBotScript />
			<main className={poppins.className}>
				<Analytics />

				<HistoryProvider>
					<I18nextProvider i18n={i18nClient}>
						<OrbisProvider locale={defaultLanguage} messages={defaultMessages[defaultLanguage]} banksList={banksList.cs} linkComponent={NextLink}>
							<QueryClientProvider client={queryClientRef.current}>
								<HydrationBoundary state={pageProps.dehydratedState}>
									{showDevtools && <Suspense fallback={null}>
											<ReactQueryDevtools />
											<RootsConsoleProduction />
										</Suspense>}
									<GrowthBookProvider growthbook={gb}>
										<NuqsAdapter>
											<NextNProgress options={{
                      showSpinner: false
                    }} />
											<Suspense fallback={null}>
												<CareerConsolePromotion />

												<ToastContainerLazy position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover icon={false} theme="colored" />
											</Suspense>
											<Component {...pageProps} />
										</NuqsAdapter>
									</GrowthBookProvider>
								</HydrationBoundary>
							</QueryClientProvider>
						</OrbisProvider>
					</I18nextProvider>
				</HistoryProvider>
			</main>
		</RootsContext.Provider>;
}